'use client';

import React from 'react';
import Link from 'next/link';
import { ChevronLeft, Leaf } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen gradient-bg">
      <div className="fixed -top-20 -right-20 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, #A8C5AD, transparent)' }} />

      <div className="max-w-2xl mx-auto px-5 py-8 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <Link
            href="/profile"
            className="w-9 h-9 rounded-xl bg-white/80 border border-border flex items-center justify-center shadow-card-sm hover:bg-white transition-colors"
          >
            <ChevronLeft size={18} className="text-foreground" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl lavender-gradient flex items-center justify-center shadow-card-sm">
              <Leaf size={14} className="text-white" />
            </div>
            <h1 className="text-lg font-extrabold text-foreground tracking-tight">Terms of Service</h1>
          </div>
        </div>

        <div className="bg-white/80 rounded-2xl border border-border shadow-card-sm p-6 space-y-6">
          <div>
            <p className="text-xs text-muted-foreground mb-4">Last updated: August 2026</p>
            <p className="text-sm text-foreground leading-relaxed">
              By using My Little Clingy, you agree to these Terms of Service. Please read them carefully.
            </p>
          </div>

          <Section title="About Clingy">
            <p>My Little Clingy is an AI-powered emotional support companion. Clingy is <strong>not</strong> a licensed therapist, psychologist, or medical professional. Clingy does not provide medical advice, diagnosis, or treatment.</p>
            <p>Clingy is designed to offer a supportive, reflective space — not to replace professional mental health care.</p>
          </Section>

          <Section title="Eligibility">
            <p>You must be at least 16 years old to use My Little Clingy. By creating an account, you confirm you meet this requirement.</p>
          </Section>

          <Section title="Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-sm text-muted-foreground">
              <li>Use Clingy to harm yourself or others</li>
              <li>Attempt to extract harmful, illegal, or abusive content</li>
              <li>Share your account with others</li>
              <li>Use the service for commercial purposes without permission</li>
            </ul>
          </Section>

          <Section title="Mental Health Disclaimer">
            <p>If you are experiencing a mental health crisis, thoughts of self-harm, or an emergency, please contact emergency services or a crisis helpline immediately. Clingy is not equipped to handle emergencies.</p>
            <p className="mt-2 font-semibold text-foreground">Crisis resources: <Link href="/safety" className="text-primary hover:underline">Safety page</Link></p>
          </Section>

          <Section title="Intellectual Property">
            <p>The My Little Clingy app, branding, and content are owned by us. Your personal data and conversations belong to you.</p>
          </Section>

          <Section title="Limitation of Liability">
            <p>My Little Clingy is provided "as is." We are not liable for any emotional distress, decisions made based on Clingy's responses, or service interruptions.</p>
          </Section>

          <Section title="Changes">
            <p>We may update these terms. Continued use after changes constitutes acceptance. We'll notify you of significant changes by email.</p>
          </Section>

          <Section title="Contact">
            <p>Questions? Email <a href="mailto:hello@mylittleclingy.com" className="text-primary hover:underline">hello@mylittleclingy.com</a></p>
          </Section>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link href="/privacy-policy" className="text-xs text-primary font-semibold hover:underline">Privacy Policy</Link>
          <span className="text-xs text-muted-foreground">·</span>
          <Link href="/safety" className="text-xs text-primary font-semibold hover:underline">Safety</Link>
          <span className="text-xs text-muted-foreground">·</span>
          <Link href="/contact" className="text-xs text-primary font-semibold hover:underline">Contact</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-extrabold text-foreground mb-2">{title}</h2>
      <div className="text-sm text-foreground leading-relaxed space-y-2">{children}</div>
    </div>
  );
}
